import { useCallback, useMemo, useState } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { buildSqafPolicyMarkdown } from "@/utils/sqaafMistralInsights";
import {
  AlertTriangle,
  BarChart3,
  Download,
  Filter,
  Info,
  MapPin,
  RefreshCw,
  Search,
  Star,
} from "lucide-react";
import { fmt2 } from "@/lib/sqaafLabels";
import {
  POLICY_PAGE_SIZE,
  buildBlockIndexBars,
  buildDomainRows,
  buildIndexTableRows,
  buildPaginationModel,
  buildPolicyInfoCard,
  buildPolicyKpis,
  buildQuestionChartRows,
  extractBlockOptions,
  filterCriticalSchools,
} from "@/utils/sqaafPolicyDerived";

const BAND_OPTIONS = ["", "Critical", "Needs Focus", "Developing", "Strong", "Exemplary"];

export default function SqafPolicyAction({
  overview,
  intervention,
  showSkeleton,
  refreshing,
  onRefresh,
  onExport,
  onBlockClick,
  onSchoolSelect,
  blockOptions = [],
}) {
  const [search, setSearch] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [bandFilter, setBandFilter] = useState("");
  const [page, setPage] = useState(0);

  const criticalSchools = intervention?.critical_schools || intervention?.tier_schools || [];
  const blockIndex = intervention?.block_intervention_index || [];

  const blockChoices = useMemo(() => {
    const extracted = extractBlockOptions(criticalSchools, blockIndex);
    if (blockOptions.length) {
      return ["", ...blockOptions];
    }
    return extracted;
  }, [criticalSchools, blockIndex, blockOptions]);

  const filteredSchools = useMemo(
    () => filterCriticalSchools(criticalSchools, { search, block: blockFilter, band: bandFilter }),
    [criticalSchools, search, blockFilter, bandFilter]
  );

  const pagination = useMemo(
    () => buildPaginationModel(page, filteredSchools.length, POLICY_PAGE_SIZE),
    [page, filteredSchools.length]
  );

  const pageRows = useMemo(() => {
    const start = pagination.current * POLICY_PAGE_SIZE;
    return filteredSchools.slice(start, start + POLICY_PAGE_SIZE);
  }, [filteredSchools, pagination.current]);

  const kpis = useMemo(() => buildPolicyKpis(intervention, overview), [intervention, overview]);
  const localInfoCard = useMemo(() => buildPolicyInfoCard(intervention, overview), [intervention, overview]);
  const criticalCount = kpis.criticalCount;

  const hasData = Boolean(overview?.n_schools || intervention?.intervention_summary);
  const buildLocalMarkdown = useCallback(
    () => buildSqafPolicyMarkdown({ overview, intervention }),
    [overview, intervention]
  );
  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        c: criticalCount,
        b: kpis.below60Count,
        block: kpis.topBlock,
      }),
    [criticalCount, kpis.below60Count, kpis.topBlock]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickHeadline,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "sqaaf-policy",
    title: "SQAAF Policy Action",
    buildLocalMarkdown,
    context: { overview, intervention },
    enabled: hasData,
    scopeKey,
  });

  const infoCard = useMemo(() => {
    if (aiLoading) return localInfoCard;
    return {
      title: pickHeadline(localInfoCard.title),
      body: pickFromBucket(localInfoCard.body, "insights", 0) || localInfoCard.body,
      isMistral,
    };
  }, [aiLoading, localInfoCard, pickHeadline, pickFromBucket, isMistral]);

  const domainRows = useMemo(() => buildDomainRows(intervention?.priority_domains), [intervention?.priority_domains]);
  const questionRows = useMemo(
    () => buildQuestionChartRows(intervention?.bottom_questions),
    [intervention?.bottom_questions]
  );
  const blockBars = useMemo(() => buildBlockIndexBars(blockIndex), [blockIndex]);
  const indexTable = useMemo(() => buildIndexTableRows(blockIndex), [blockIndex]);

  if (showSkeleton && !overview) {
    return <div className="sq-exec-skeleton" data-testid="sqaf-policy-skeleton" />;
  }

  return (
    <>
      <section className="page-header" id="actions">
        <div>
          <div className="eyebrow">
            <AlertTriangle className="svg-icon" strokeWidth={2} />
            Policy action · Pune District · 2025–26
          </div>
          <h1>Policy Action</h1>
          <p>
            Prioritize critical schools, weakest questions, priority domains and block-level intervention workload
            from one decision-ready view.
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="ghost-action" onClick={onExport}>
            <Download className="svg-icon" strokeWidth={2} />
            Download CSV
          </button>
          <button type="button" className="primary-action" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={`svg-icon ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </section>

      <section className="summary-row">
        <article className="kpi-card">
          <div className="kpi-icon red">
            <AlertTriangle className="large-icon" strokeWidth={2} />
          </div>
          <div>
            <small>Critical Schools</small>
            <strong>{kpis.criticalCount.toLocaleString()}</strong>
            <span>Score ≤ 40</span>
          </div>
        </article>
        <article className="kpi-card">
          <div className="kpi-icon orange">
            <BarChart3 className="large-icon" strokeWidth={2} />
          </div>
          <div>
            <small>Schools &lt; 60%</small>
            <strong>{kpis.below60Count.toLocaleString()}</strong>
            <span>Sub-60% schools</span>
          </div>
        </article>
        <article className="kpi-card">
          <div className="kpi-icon">
            <Star className="large-icon" strokeWidth={2} />
          </div>
          <div>
            <small>Priority Sections</small>
            <strong>{kpis.priorityCount}</strong>
            <span>Bottom priority domains</span>
          </div>
        </article>
        <article className="kpi-card">
          <div className="kpi-icon purple">
            <MapPin className="large-icon" strokeWidth={2} />
          </div>
          <div>
            <small>Highest Intervention Block</small>
            <strong>{kpis.topBlock}</strong>
            <span>Index {kpis.topBlockIndex}</span>
          </div>
        </article>
        <article className="info-card">
          <div className="kpi-icon">
            <Info className="svg-icon" strokeWidth={2} />
          </div>
          <div>
            <b>
              {infoCard.title}
              {infoCard.isMistral ? " · Mistral AI" : ""}
            </b>
            <p>{infoCard.body}</p>
          </div>
        </article>
      </section>

      <section className="dashboard-grid">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h2>Critical Schools (Score ≤ 40)</h2>
              <p>Search and filter schools that need direct review, ownership and remediation tracking.</p>
            </div>
            <div className="panel-tools">
              <label className="search-box">
                <Search className="svg-icon" strokeWidth={2} />
                <input
                  placeholder="Search school name or code..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  aria-label="Search critical schools"
                />
              </label>
              <label className="select-chip">
                <select
                  className="sq-inline-select"
                  value={blockFilter}
                  onChange={(e) => {
                    setBlockFilter(e.target.value);
                    setPage(0);
                  }}
                  aria-label="Filter by block"
                >
                  {blockChoices.map((b) => (
                    <option key={b || "all"} value={b}>
                      {b || "All Blocks"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="select-chip">
                <select
                  className="sq-inline-select"
                  value={bandFilter}
                  onChange={(e) => {
                    setBandFilter(e.target.value);
                    setPage(0);
                  }}
                  aria-label="Filter by band"
                >
                  {BAND_OPTIONS.map((b) => (
                    <option key={b || "all"} value={b}>
                      {b || "All Bands"}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="icon-button" title="Filter" aria-label="Filter">
                <Filter className="svg-icon" strokeWidth={2} />
              </button>
            </div>
          </div>
          <div className="table-wrap">
            <table aria-label="Critical schools table">
              <thead>
                <tr>
                  <th>School Code</th>
                  <th>School Name</th>
                  <th>Block</th>
                  <th>Answered %</th>
                  <th style={{ textAlign: "right" }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={5}>No critical schools match the current filters.</td>
                  </tr>
                ) : (
                  pageRows.map((s) => (
                    <tr
                      key={s.school_code}
                      className="sq-policy-row"
                      onClick={() => onSchoolSelect?.(s.school_code)}
                    >
                      <td>
                        <b>{s.school_code}</b>
                      </td>
                      <td>{(s.school_name || "").toUpperCase()}</td>
                      <td>{s.block || "—"}</td>
                      <td>{fmt2(s["Answered_%"])}%</td>
                      <td className="score-danger">{fmt2(s["SQAAF_Score_%"])}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            <span>
              Showing {pagination.start} to {pagination.end} of {filteredSchools.length} results
            </span>
            <div className="pagination">
              <button
                type="button"
                className="page-btn"
                disabled={pagination.current === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Previous page"
              >
                ‹
              </button>
              {pagination.pages.map((p, i) =>
                typeof p === "string" ? (
                  <span key={`${p}-${i}`}>…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={`page-btn ${pagination.current === p ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p + 1}
                  </button>
                )
              )}
              <button
                type="button"
                className="page-btn"
                disabled={pagination.current >= pagination.totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        </article>

        <div className="right-stack">
          <article className="panel">
            <div className="panel-head">
              <div>
                <h3>Priority Domains (Bottom 5 Sections)</h3>
                <p>Focus improvement here first.</p>
              </div>
            </div>
            <div className="domain-list">
              {domainRows.length === 0 ? (
                <div className="domain-row">
                  <span>No priority domains available</span>
                  <strong>—</strong>
                </div>
              ) : (
                domainRows.map((row) => (
                  <div key={row.label} className="domain-row">
                    <span>{row.label}</span>
                    <strong>{row.mean}</strong>
                  </div>
                ))
              )}
            </div>
          </article>

          <article className="panel">
            <div className="panel-head">
              <div>
                <h3>Bottom 10 Questions (Overall Mean)</h3>
                <p>Pareto view of the lowest-scoring questions.</p>
              </div>
            </div>
            <div className="q-chart">
              {questionRows.length === 0 ? (
                <div className="q-row">
                  <span>No bottom questions available</span>
                  <div className="q-track" />
                  <b>—</b>
                </div>
              ) : (
                questionRows.map((row) => (
                  <div key={row.fullLabel} className="q-row" title={row.fullLabel}>
                    <span>{row.label}</span>
                    <div className="q-track">
                      <i style={{ width: `${row.width}%` }} />
                    </div>
                    <b>{row.score}</b>
                  </div>
                ))
              )}
              <div className="chart-axis">
                <span>0</span>
                <span>20</span>
                <span>40</span>
                <span>60</span>
                <span>80</span>
                <span>100</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="panel wide-card">
        <div className="chart-head">
          <div>
            <h2>Block Intervention Priority Index</h2>
            <p>0.6 × (% schools &lt;60) + 0.4 × (% critical). Higher score = higher intervention need.</p>
          </div>
        </div>
        <div className="priority-chart" aria-label="Block intervention priority index chart">
          {blockBars.map((bar) => (
            <button
              key={bar.block}
              type="button"
              className="block-bar"
              title={bar.title}
              onClick={() => onBlockClick?.(bar.block)}
            >
              <b>{bar.index}</b>
              <i style={{ height: `${bar.heightPct}%` }} />
              <span>{bar.block}</span>
            </button>
          ))}
        </div>
        <div className="index-table">
          <table aria-label="Block intervention index table">
            <thead>
              <tr>
                <th>Block</th>
                {indexTable.blocks.map((block) => (
                  <th key={block}>{block}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {indexTable.rows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  {row.values.map((value, i) => (
                    <td key={`${row.label}-${indexTable.blocks[i]}`}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
